import React, { MouseEvent, useState } from 'react';
import { useGetMessageTemplatesQuery } from '../../../../app/Slices/SevenDayApiSlice';
import {
    Box,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { AddCircle, Delete } from '@mui/icons-material';
import ScheduledMessagesModal from './ScheduledMessagesModal';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import { MessageTemplate } from '../../../types/messaging';
import DeleteTemplateModal from './DeleteTemplateModal';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { MessageTemplateEvent } from '../../../enums/messaging';

const ScheduledMessages = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isIpad = useMediaQuery(theme.breakpoints.down('lg'));

    const { data: templates } = useGetMessageTemplatesQuery();

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [pendingEdit, setPendingEdit] = useState<MessageTemplate | null>(
        null,
    );
    const [pendingDelete, setPendingDelete] = useState<MessageTemplate | null>(
        null,
    );

    const handleDelete = (
        event: MouseEvent<HTMLButtonElement>,
        template: MessageTemplate,
    ) => {
        event.stopPropagation();
        setPendingDelete(template);
    };

    const renderInterval = (template: MessageTemplate) => {
        const event =
            template.event === MessageTemplateEvent.Checkin
                ? 'check-in'
                : template.event === MessageTemplateEvent.Completed
                  ? 'checkout'
                  : template.event === MessageTemplateEvent.Approved
                    ? 'confirmation'
                    : 'cancellation';

        if (template.interval === 0) {
            return `On ${event}`;
        }

        return `${Math.abs(template.interval)} day${Math.abs(template.interval) > 1 ? 's' : ''} ${template.interval > 0 ? 'after' : 'before'} ${event}`;
    };

    return (
        <Stack spacing={2} sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='body3' sx={{ color: 'text.primary' }}>
                    Manage message templates to send scheduled messages to
                    guests
                </Typography>
                {isMobile || isIpad ? (
                    <CustomIconButton
                        variant='contained'
                        color='primary'
                        onClick={() => setEditModalOpen(true)}
                    >
                        <AddCircle />
                    </CustomIconButton>
                ) : (
                    <CustomButton
                        variant='contained'
                        color='primary'
                        startIcon={<AddCircle />}
                        onClick={() => setEditModalOpen(true)}
                    >
                        New Template
                    </CustomButton>
                )}
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Message</TableCell>
                            <TableCell padding='checkbox'>
                                Experiences
                            </TableCell>
                            <TableCell>Scheduled</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell padding='checkbox' />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {templates?.map((template, index) => (
                            <TableRow
                                key={index}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setPendingEdit(template);
                                    setEditModalOpen(true);
                                }}
                            >
                                <TableCell>{template.name}</TableCell>
                                <TableCell>{template.message}</TableCell>
                                <TableCell padding='checkbox'>
                                    {template.modules.length > 0
                                        ? template.modules.length
                                        : 'All'}
                                </TableCell>
                                <TableCell>
                                    {renderInterval(template)}
                                </TableCell>
                                <TableCell>
                                    <CustomChip
                                        variant='outlined'
                                        color={
                                            template.isDraft
                                                ? 'default'
                                                : 'success'
                                        }
                                        label={
                                            template.isDraft
                                                ? 'Draft'
                                                : 'Active'
                                        }
                                    />
                                </TableCell>
                                <TableCell padding='checkbox'>
                                    <IconButton
                                        color='error'
                                        onClick={(e) =>
                                            handleDelete(e, template)
                                        }
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ScheduledMessagesModal
                open={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setPendingEdit(null);
                }}
                template={pendingEdit}
            />
            <DeleteTemplateModal
                open={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                template={pendingDelete}
            />
        </Stack>
    );
};

export default ScheduledMessages;
